import type { AgendaEvent, AgendaProfile } from '../../lib/api'
import { joursCouverts } from './AgendaScreen'

/**
 * Les barres d'une semaine de la grille.
 *
 * Un profil peut s'afficher en barre plutôt qu'en couleur de case : « Vacances du 15 au 21 » est
 * une durée, pas sept journées sans rapport. La barre traverse donc les colonnes d'un seul tenant.
 *
 * Une barre ne vit qu'à l'intérieur d'une semaine : un séjour à cheval sur deux semaines en
 * produit deux, chacune dans sa rangée. C'est ce que la grille peut dessiner — une barre qui
 * saute à la ligne passerait par la gauche de l'écran.
 */
export type BarreDeSemaine = {
  readonly profileId: string
  readonly hue: number
  readonly titre: string
  /** Colonnes de grille CSS, de 1 à 7. */
  readonly premiereColonne: number
  readonly derniereColonne: number
  /** Faux : la barre vient de la semaine précédente, son bord gauche reste droit. */
  readonly commence: boolean
  /** Faux : elle se poursuit la semaine suivante. */
  readonly finit: boolean
  /** Rangée dans laquelle la poser, pour que deux barres ne se recouvrent pas. */
  readonly rangee: number
  /**
   * Ce segment-ci porte le titre.
   *
   * Un seul par séjour et par mois affiché : répété à chaque semaine, il encombrait la grille,
   * et les segments d'un ou deux jours n'en montraient qu'un morceau coupé — « Paren ». Il est
   * donc posé sur le segment le plus large, celui qui a la place de le lire (correction S82).
   */
  readonly porteLeTitre: boolean
}

/**
 * Calcule les barres à dessiner au-dessus d'une semaine.
 *
 * @param joursDeLaSemaine sept clés de jour, ou `null` pour les cases d'avant le 1er et d'après
 *   le dernier.
 * @param evenements les événements déjà filtrés — ce qui est masqué n'arrive pas ici.
 * @param profilsEnBarre les profils réglés sur « barre ».
 */
export function barresDeLaSemaine(
  joursDeLaSemaine: readonly (string | null)[],
  evenements: readonly AgendaEvent[],
  profilsEnBarre: readonly AgendaProfile[],
): readonly BarreDeSemaine[] {
  const brutes: Omit<BarreDeSemaine, 'rangee' | 'porteLeTitre'>[] = []

  for (const evenement of evenements) {
    const profil = profilsEnBarre.find((candidat) => candidat.id === evenement.profileId)

    if (!profil) {
      continue
    }

    const couverts = joursCouverts(evenement)
    const premierJour = couverts[0]
    const dernierJour = couverts[couverts.length - 1]

    // Les suites de colonnes consécutives où ce jour est couvert. Une seule, sauf si la semaine
    // commence ou finit au milieu du séjour.
    let debut: number | null = null

    for (let colonne = 0; colonne <= 7; colonne += 1) {
      const jour = colonne < 7 ? joursDeLaSemaine[colonne] : null
      const dedans = jour !== null && jour !== undefined && couverts.includes(jour)

      if (dedans && debut === null) {
        debut = colonne
      }

      if (!dedans && debut !== null) {
        brutes.push({
          profileId: profil.id,
          hue: profil.hue,
          titre: evenement.title,
          premiereColonne: debut + 1,
          derniereColonne: colonne,
          commence: joursDeLaSemaine[debut] === premierJour,
          finit: joursDeLaSemaine[colonne - 1] === dernierJour,
        })

        debut = null
      }
    }
  }

  // Rangées : la première libre où la barre ne recouvre rien. Deux séjours qui se chevauchent
  // tiennent ainsi l'un sous l'autre au lieu de se masquer.
  const occupees: { debut: number; fin: number }[][] = []
  const posees: BarreDeSemaine[] = []

  for (const barre of brutes) {
    let rangee = 0

    while (
      (occupees[rangee] ?? []).some(
        (autre) =>
          autre.debut <= barre.derniereColonne && autre.fin >= barre.premiereColonne,
      )
    ) {
      rangee += 1
    }

    occupees[rangee] = [
      ...(occupees[rangee] ?? []),
      { debut: barre.premiereColonne, fin: barre.derniereColonne },
    ]
    // Le titre est décidé à l'échelle du mois, par barresDuMois : ici, par défaut, personne.
    posees.push({ ...barre, rangee, porteLeTitre: false })
  }

  return posees
}

/**
 * Découpe un mois en semaines de sept cases, lundi en premier.
 *
 * @param decalage nombre de cases vides avant le 1er.
 * @param nombreDeJours jours du mois.
 * @param cle donne la clé d'un jour, de 1 au dernier.
 */
export function semainesDuMois(
  decalage: number,
  nombreDeJours: number,
  cle: (jour: number) => string,
): readonly (readonly (string | null)[])[] {
  const cases: (string | null)[] = [
    ...Array.from({ length: decalage }, () => null),
    ...Array.from({ length: nombreDeJours }, (_, index) => cle(index + 1)),
  ]

  while (cases.length % 7 !== 0) {
    cases.push(null)
  }

  return Array.from({ length: cases.length / 7 }, (_, rang) =>
    cases.slice(rang * 7, rang * 7 + 7),
  )
}

/**
 * Les barres de tout un mois, avec le titre posé une fois par séjour.
 *
 * Calculées pour le mois entier et non semaine par semaine : savoir quel segment est le plus
 * large suppose de les avoir tous sous les yeux.
 */
export function barresDuMois(
  semaines: readonly (readonly (string | null)[])[],
  evenements: readonly AgendaEvent[],
  profilsEnBarre: readonly AgendaProfile[],
): readonly (readonly BarreDeSemaine[])[] {
  const parSemaine = semaines.map((semaine) =>
    barresDeLaSemaine(semaine, evenements, profilsEnBarre),
  )

  // Le segment le plus large de chaque séjour, dans ce mois-ci. À égalité, le premier.
  const meilleur = new Map<string, { semaine: number; rang: number; largeur: number }>()

  parSemaine.forEach((barres, semaine) => {
    barres.forEach((barre, rang) => {
      const identite = `${barre.profileId}|${barre.titre}`
      const largeur = barre.derniereColonne - barre.premiereColonne + 1
      const tenant = meilleur.get(identite)

      if (!tenant || largeur > tenant.largeur) {
        meilleur.set(identite, { semaine, rang, largeur })
      }
    })
  })

  return parSemaine.map((barres, semaine) =>
    barres.map((barre, rang) => {
      const tenant = meilleur.get(`${barre.profileId}|${barre.titre}`)

      return {
        ...barre,
        porteLeTitre: tenant?.semaine === semaine && tenant.rang === rang,
      }
    }),
  )
}
